import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ADVANCED_VIDEO_LIMITS,
  BRIEF_THEMES,
  LIMITS,
  MUSIC_ASSET_TYPES,
  type Asset,
  type AspectRatio,
  type BriefTheme,
} from '@gc/shared';
import { ApiError, apiRequest } from '../api/client.js';
import { listarAssets, subirAsset } from '../api/assets.js';
import { actualizarProyecto, crearProyecto } from '../api/projects.js';

/**
 * Punto de partida por temática. Es solo un arranque: el Director creativo, ya
 * en el proyecto, lo desarrolla en un brief completo.
 */
const PLANTILLAS: Record<BriefTheme, string> = {
  viajes: 'Un recorrido visual por [destino]: sus paisajes, su gente y sus rincones menos conocidos, con música ambiental y ritmo contemplativo.',
  gastronomia: 'La preparación de [plato o producto] paso a paso, del ingrediente al emplatado, con detalle de texturas y luz cálida de cocina.',
  educacion: 'Explicar [tema] en tres minutos: qué es, por qué importa y un ejemplo cotidiano, con imágenes claras y una narración cercana.',
  tecnologia: 'Presentar [producto o servicio]: el problema que resuelve, cómo funciona y a quién ayuda, con un estilo limpio y moderno.',
  naturaleza: 'La vida en [ecosistema o especie] a lo largo de un día, con planos amplios y detalles, y música orgánica.',
  historia: 'La historia de [lugar, persona o acontecimiento] contada en orden cronológico, con imágenes de época y tono documental.',
  bienestar: 'Una rutina de [práctica] para empezar el día, con imágenes serenas, luz suave y música relajante.',
  empresa: 'Quiénes somos en [empresa]: lo que hacemos, cómo lo hacemos y qué nos diferencia, en un tono profesional y humano.',
  inmobiliaria: 'Visita visual a [inmueble o proyecto]: exteriores, estancias y entorno, con luz natural y música elegante.',
  eventos: 'Anuncio de [evento]: fecha, lugar, qué va a pasar y por qué no hay que perdérselo, con energía y ritmo.',
  moda: 'Una colección de [marca o estilo] en escenarios urbanos y naturales, con fotografía editorial y música actual.',
  deporte: 'La preparación y el reto de [deporte o prueba]: esfuerzo, paisaje y superación, con montaje dinámico.',
  arte: 'El proceso creativo de [disciplina o artista]: de la idea a la obra, con detalle de materiales y manos trabajando.',
  infantil: 'Una historia sencilla sobre [personaje o valor] para niños, con colores vivos, imágenes amables y narración pausada.',
  documental: 'Testimonio sobre [tema]: contexto, voces y lugares, con imágenes reales y tono sobrio.',
  otro: '',
};

/** De dónde sale la pista: generada, elegida de la biblioteca o subida ahora. */
type FuenteMusica = 'auto' | 'biblioteca' | 'archivo';

const SEGUNDO = 1_000;

export function NuevoProyecto(): React.JSX.Element {
  const navigate = useNavigate();
  const [parametros] = useSearchParams();
  // Fase 09: ?tipo=ADVANCED_VIDEO crea un proyecto de clips generados.
  const tipoPedido = parametros.get('tipo');
  const tipo = tipoPedido === 'ADVANCED_VIDEO' || tipoPedido === 'VIRTUAL_CREATOR' ? tipoPedido : 'VIDEO_PODCAST';
  // Fases 09 y 10 comparten límites (15–60 s, 2–8 planos) y pipeline de clips.
  const avanzado = tipo !== 'VIDEO_PODCAST';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tema, setTema] = useState<BriefTheme>('otro');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const [duracionSegundos, setDuracionSegundos] = useState(tipoPedido === 'ADVANCED_VIDEO' || tipoPedido === 'VIRTUAL_CREATOR' ? 24 : 60);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [fuenteMusica, setFuenteMusica] = useState<FuenteMusica>('biblioteca');
  const [pistaId, setPistaId] = useState('');
  const [archivoPista, setArchivoPista] = useState<File | null>(null);
  const [pistas, setPistas] = useState<Asset[]>([]);
  const [musicaGenerable, setMusicaGenerable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // La pista se puede elegir ya aquí: la biblioteca es del propietario, no del
  // proyecto. Si hay proveedor de música, generarla es la opción por defecto.
  useEffect(() => {
    let vigente = true;
    void (async () => {
      try {
        const [vista, ...paginas] = await Promise.all([
          apiRequest<{ runtime: { capability: string; available: boolean }[] }>('/capabilities'),
          ...MUSIC_ASSET_TYPES.map((type) => listarAssets({ type, limit: 50 })),
        ]);
        if (!vigente) return;
        const disponible = vista.runtime.some((c) => c.capability === 'MUSIC' && c.available);
        setMusicaGenerable(disponible);
        if (disponible) setFuenteMusica('auto');
        setPistas(
          paginas
            .flatMap((pagina) => pagina.data)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        );
      } catch {
        if (vigente) setMusicaGenerable(false);
      }
    })();
    return () => {
      vigente = false;
    };
  }, []);

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const proyecto = await crearProyecto({
        name,
        description,
        type: tipo,
        settings: {
          aspectRatio,
          targetDurationMs: duracionSegundos * SEGUNDO,
          musicEnabled,
          voiceEnabled,
        },
      });
      // La pista se asigna después de crear: el asset se asocia al proyecto y
      // la revisión sube a 2. Si esto falla, el proyecto ya existe y la música
      // se puede elegir en su página, así que no se bloquea la navegación.
      if (musicEnabled && fuenteMusica !== 'auto') {
        try {
          const assetId =
            fuenteMusica === 'archivo' && archivoPista !== null
              ? (await subirAsset(proyecto.id, archivoPista, {})).id
              : pistaId || null;
          if (assetId !== null) {
            await actualizarProyecto(proyecto.id, {
              expectedRevision: proyecto.revision,
              settings: { musicAssetId: assetId },
            });
          }
        } catch {
          // Se informa en la página del proyecto (sin pista no se puede aprobar).
        }
      }
      void navigate(`/proyectos/${proyecto.id}`, { replace: true });
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo crear el proyecto.');
    } finally {
      setEnviando(false);
    }
  }

  const minSegundos = (avanzado ? ADVANCED_VIDEO_LIMITS.totalDurationMinMs : LIMITS.totalDurationMinMs) / SEGUNDO;
  const maxSegundos = (avanzado ? ADVANCED_VIDEO_LIMITS.totalDurationMaxMs : LIMITS.totalDurationMaxMs) / SEGUNDO;

  return (
    <section>
      <h1>{tipo === 'VIRTUAL_CREATOR' ? 'Nuevo proyecto de Creador virtual' : avanzado ? 'Nuevo proyecto de Vídeo avanzado' : 'Nuevo proyecto de Vídeo / Podcast'}</h1>
      {avanzado && (
        <p>
          <small>
            {tipo === 'VIRTUAL_CREATOR'
              ? 'Una identidad virtual aprobada protagoniza clips generados con IA en la localización que definas, con revisión plano a plano y presupuesto visible. '
              : 'Clips generados con IA a partir de un storyboard y de imágenes de referencia, con revisión plano a plano y presupuesto visible. '}
            Entre {minSegundos} y {maxSegundos} segundos, de 2 a 8 planos.
          </small>
        </p>
      )}

      <form onSubmit={(evento) => void enviar(evento)} noValidate>
        <p>
          <label htmlFor="name">Nombre</label>
          <br />
          <input
            id="name"
            value={name}
            onChange={(evento) => setName(evento.target.value)}
            maxLength={LIMITS.nameMaxLength}
            required
            style={{ width: '100%', maxWidth: '30rem' }}
          />
        </p>

        <p>
          <label htmlFor="tema">Temática</label>
          <br />
          <select
            id="tema"
            value={tema}
            onChange={(evento) => {
              const elegido = evento.target.value as BriefTheme;
              setTema(elegido);
              // Solo se precarga si la idea está vacía o es otra plantilla: lo
              // escrito por el usuario no se pisa.
              const esPlantilla = description === '' || Object.values(PLANTILLAS).includes(description);
              if (esPlantilla && PLANTILLAS[elegido] !== '') setDescription(PLANTILLAS[elegido]);
            }}
          >
            {Object.entries(BRIEF_THEMES).map(([clave, etiqueta]) => (
              <option key={clave} value={clave}>
                {etiqueta}
              </option>
            ))}
          </select>
          <br />
          <small>
            Precarga una idea de partida. En el proyecto, el <strong>director creativo</strong> la
            desarrolla en un brief completo.
          </small>
        </p>

        <p>
          <label htmlFor="description">Idea</label>
          <br />
          <textarea
            id="description"
            value={description}
            onChange={(evento) => setDescription(evento.target.value)}
            maxLength={LIMITS.descriptionMaxLength}
            required
            rows={5}
            style={{ width: '100%', maxWidth: '40rem' }}
            placeholder="Un vídeo cinematográfico de tres minutos sobre un viaje por Japón con música ambiental."
          />
          <br />
          <small>
            {description.length} / {LIMITS.descriptionMaxLength} caracteres
          </small>
        </p>

        <fieldset>
          <legend>Formato</legend>
          {(['16:9', '9:16'] as const).map((valor) => (
            <label key={valor} style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                name="aspectRatio"
                value={valor}
                checked={aspectRatio === valor}
                onChange={() => setAspectRatio(valor)}
              />{' '}
              {valor === '16:9' ? '16:9 horizontal (1920×1080)' : '9:16 vertical (1080×1920)'}
            </label>
          ))}
        </fieldset>

        <p>
          <label htmlFor="duracion">
            Duración objetivo: {duracionSegundos} s
          </label>
          <br />
          <input
            id="duracion"
            type="range"
            min={minSegundos}
            max={maxSegundos}
            step={avanzado ? 1 : 5}
            value={duracionSegundos}
            onChange={(evento) => setDuracionSegundos(Number(evento.target.value))}
            style={{ width: '100%', maxWidth: '30rem' }}
          />
          <br />
          <small>
            Entre {minSegundos} y {maxSegundos} segundos.
          </small>
        </p>

        <fieldset>
          <legend>Audio</legend>
          <label style={{ display: 'block' }}>
            <input
              type="checkbox"
              checked={musicEnabled}
              onChange={(evento) => setMusicEnabled(evento.target.checked)}
            />{' '}
            Incluir música de fondo
          </label>
          {musicEnabled && (
            <div style={{ margin: '0.5rem 0 0.75rem 1.5rem' }}>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="fuenteMusica"
                  value="auto"
                  checked={fuenteMusica === 'auto'}
                  disabled={musicaGenerable !== true}
                  onChange={() => setFuenteMusica('auto')}
                />{' '}
                Generarla automáticamente
                {musicaGenerable === false && (
                  <small> — sin proveedor de música generativa configurado</small>
                )}
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="fuenteMusica"
                  value="biblioteca"
                  checked={fuenteMusica === 'biblioteca'}
                  onChange={() => setFuenteMusica('biblioteca')}
                />{' '}
                Elegir una pista de la biblioteca
              </label>
              {fuenteMusica === 'biblioteca' && (
                <p style={{ margin: '0.25rem 0 0.5rem 1.5rem' }}>
                  <select
                    aria-label="Pista de la biblioteca"
                    value={pistaId}
                    onChange={(evento) => setPistaId(evento.target.value)}
                    style={{ maxWidth: '26rem' }}
                  >
                    <option value="">— elegir después, en el proyecto —</option>
                    {pistas.map((pista) => (
                      <option key={pista.id} value={pista.id}>
                        {pista.filename}
                        {pista.metadata.durationMs !== undefined
                          ? ` (${Math.round(pista.metadata.durationMs / 1000)} s)`
                          : ''}
                      </option>
                    ))}
                  </select>
                  {pistas.length === 0 && (
                    <>
                      <br />
                      <small>La biblioteca aún no tiene pistas de audio.</small>
                    </>
                  )}
                </p>
              )}
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="fuenteMusica"
                  value="archivo"
                  checked={fuenteMusica === 'archivo'}
                  onChange={() => setFuenteMusica('archivo')}
                />{' '}
                Subir una pista nueva
              </label>
              {fuenteMusica === 'archivo' && (
                <p style={{ margin: '0.25rem 0 0.5rem 1.5rem' }}>
                  <input
                    aria-label="Archivo de música"
                    type="file"
                    accept="audio/mpeg,audio/wav,audio/aac,audio/mp4,.mp3,.wav,.aac,.m4a"
                    onChange={(evento) => setArchivoPista(evento.target.files?.[0] ?? null)}
                  />
                  <br />
                  <small>Se sube al crear el proyecto y queda en tu biblioteca para reutilizarla.</small>
                </p>
              )}
            </div>
          )}
          <label style={{ display: 'block' }}>
            <input
              type="checkbox"
              checked={voiceEnabled}
              onChange={(evento) => setVoiceEnabled(evento.target.checked)}
            />{' '}
            Incluir narración
          </label>
          {voiceEnabled && (
            <p style={{ margin: '0.25rem 0 0 1.5rem' }}>
              <small>
                La locución se genera por escena a partir de su texto. En cada escena podrás
                sustituirla por un archivo de la biblioteca o subir uno propio.
              </small>
            </p>
          )}
        </fieldset>

        {error !== null && (
          <p role="alert" style={{ color: 'var(--error)' }}>
            {error}
          </p>
        )}

        <p>
          <button type="submit" disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear proyecto'}
          </button>{' '}
          <button type="button" onClick={() => void navigate('/')} disabled={enviando}>
            Cancelar
          </button>
        </p>
      </form>

      <p>
        <small>
          Crear el proyecto no llama a la IA ni gasta crédito: la generación de estructura,
          imágenes y audio se lanza después, desde la página del proyecto.
        </small>
      </p>
    </section>
  );
}
